import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft, 
  BarChart3, 
  Copy, 
  Loader2, 
  DollarSign, 
  AlertCircle, 
  CheckCircle,
  FileText,
  FileSpreadsheet,
  Edit3,
  Save,
  X
} from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { createPageUrl } from "@/utils";
// Import jsPDF corretamente como módulo
import { jsPDF } from "jspdf";
// Importar autotable explicitamente
import 'jspdf-autotable';

export default function VariavelRevenue() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [revenue, setRevenue] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [monthsCount, setMonthsCount] = useState(0); // Calculado automaticamente baseado nas datas
  const [isEditing, setIsEditing] = useState(false);
  const [isCnpjEditing, setIsCnpjEditing] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [error, setError] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [csvGenerating, setCsvGenerating] = useState(false);
  const [editingValues, setEditingValues] = useState(false);
  const [editedValues, setEditedValues] = useState([]);

  // Função para formatar data MM/AAAA
  const formatDate = (value) => {
    if (!value) return '';
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 2) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      const month = cleaned.substring(0, 2);
      const year = cleaned.substring(2, 6);
      return `${month}/${year}`;
    }
    return value;
  };

  // Função para calcular quantidade de meses entre duas datas
  const calculateMonthsDifference = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    
    const [startMonth, startYear] = startDate.split('/').map(num => parseInt(num));
    const [endMonth, endYear] = endDate.split('/').map(num => parseInt(num));
    
    if (!startMonth || !startYear || !endMonth || !endYear) return 0;
    if (startMonth < 1 || startMonth > 12 || endMonth < 1 || endMonth > 12) return 0;
    
    const monthsDiff = (endYear - startYear) * 12 + (endMonth - startMonth) + 1;
    return monthsDiff > 0 ? monthsDiff : 0;
  };

  // Atualizar quantidade de meses quando as datas mudarem
  React.useEffect(() => {
    const months = calculateMonthsDifference(startDate, endDate);
    setMonthsCount(months);
  }, [startDate, endDate]);

  // Função para gerar percentual adicional aleatório entre 1,86584% e 2,48595%
  const generateRandomBonus = () => {
    const min = 1.86584;
    const max = 2.48595;
    return Math.random() * (max - min) + min;
  };

  // Função para gerar pesos mensais baseados no código Python
  const getMonthlyWeights = () => {
    const baseWeights = [
      1.2,  // Janeiro (ajuste pós-feriados)
      0.8,  // Fevereiro (tipicamente mais lento)
      1.1,  // Março (fim do ano fiscal)
      0.9,  // Abril
      1.0,  // Maio
      1.1,  // Junho (meio do ano)
      0.9,  // Julho
      0.8,  // Agosto (temporada de férias)
      1.2,  // Setembro (volta aos negócios)
      1.1,  // Outubro
      1.0,  // Novembro
      1.3   // Dezembro (temporada de feriados)
    ];
    
    // Adicionar aleatoriedade controlada aos pesos
    const noisyWeights = baseWeights.map(weight => {
      const noise = (Math.random() - 0.5) * 0.1; // Ruído controlado
      return Math.max(0.5, weight + noise); // Garantir peso mínimo de 0.5
    });
    
    // NÃO normalizar - os pesos devem variar em torno de 1.0, não somar 1.0
    // Cada peso representa uma variação da média mensal (ex: 1.2 = 120% da média)
    console.log("🏷️ Pesos mensais brutos:", noisyWeights.slice(0, 12).map(w => w.toFixed(3)));
    return noisyWeights;
  };

  // Função para gerar percentuais aleatórios que somam 100%
  const generateRandomPercentages = (count = 12, minPercent = 6.0, maxPercent = 9.0) => {
    console.log("🎲 Gerando percentuais aleatórios baseados no algoritmo Python...");
    const percentages = new Array(count);
    
    // Gerar distribuição inicial balanceada
    for (let i = 0; i < count; i++) {
      percentages[i] = 100 / count;
    }
    
    // Aplicar perturbação controlada (algoritmo do Python adaptado)
    for (let iteration = 0; iteration < 500; iteration++) {
      const newPercentages = percentages.map(p => {
        const noise = (Math.random() - 0.5) * 0.6; // Ruído controlado
        return p + noise;
      });
      
      // Normalizar para somar 100%
      const sum = newPercentages.reduce((acc, val) => acc + val, 0);
      const normalized = newPercentages.map(p => (p / sum) * 100);
      
      // Verificar se está dentro dos limites (6% a 9% por mês, máximo 15%)
      const withinLimits = normalized.every(p => 
        p >= minPercent && p <= maxPercent && p <= 15
      );
      
      if (withinLimits) {
        // Verificar se meses consecutivos não são muito similares
        let validDistribution = true;
        for (let i = 0; i < normalized.length - 1; i++) {
          if (Math.abs(normalized[i] - normalized[i + 1]) < 0.5) {
            validDistribution = false;
            break;
          }
        }
        
        if (validDistribution) {
          console.log("✅ Percentuais gerados com sucesso:", normalized.map(p => p.toFixed(2) + "%"));
          return normalized;
        }
      }
      
      // Ajustar gradualmente
      for (let i = 0; i < count; i++) {
        percentages[i] = Math.max(minPercent, Math.min(maxPercent, normalized[i]));
      }
      
      // Renormalizar
      const adjustedSum = percentages.reduce((acc, val) => acc + val, 0);
      for (let i = 0; i < count; i++) {
        percentages[i] = (percentages[i] / adjustedSum) * 100;
      }
    }
    
    console.log("⚠️ Usando distribuição de fallback");
    return percentages;
  };

  // Nova função para distribuir receita baseada na média de 12 meses
  const distributeVariableRevenue = (inputRevenue, months) => {
    console.log(`🔄 Iniciando distribuição variável para ${months} meses...`);
    console.log("💰 Valor de entrada (parâmetro):", inputRevenue);
    
    // NOVA LÓGICA: Dividir o valor por 12 para obter a média mensal
    const monthlyAverage = inputRevenue / 12;
    console.log("📊 Média mensal (valor/12):", monthlyAverage.toFixed(2));
    
    // Obter pesos mensais e percentuais aleatórios
    const weights = getMonthlyWeights();
    const percentages = generateRandomPercentages(months);
    
    // CORREÇÃO: Nova lógica - aplicar variação percentual na média mensal
    console.log("🔧 Percentuais gerados:", percentages.map(p => p.toFixed(2) + "%"));
    console.log("⚖️ Pesos mensais:", weights.slice(0, months).map(w => w.toFixed(3)));
    
    const monthlyValues = [];
    let runningTotal = 0;
    
    // Calcular total esperado (média × meses)
    const totalExpected = monthlyAverage * months;
    console.log("🎯 Total esperado (média × meses):", totalExpected.toFixed(2));
    
    // NOVA ABORDAGEM: Calcular todos os meses primeiro, depois normalizar
    const tempValues = [];
    let tempTotal = 0;
    
    // Calcular valores para TODOS os meses
    for (let i = 0; i < months; i++) {
      console.log(`\n📅 Processando mês ${i + 1}:`);
      
      // 1. Aplicar peso mensal na média (agora usando pesos corretos ~1.0)
      const weightedAverage = monthlyAverage * weights[i % 12];
      console.log(`   - Média com peso: ${monthlyAverage.toFixed(2)} × ${weights[i % 12].toFixed(3)} = ${weightedAverage.toFixed(2)}`);
      
      // 2. Aplicar variação percentual adicional (pequena variação de ±10%)
      const variationFactor = 0.9 + (percentages[i] / 100) * 0.2; // Variação entre 90% e 110%
      const variedValue = weightedAverage * variationFactor;
      console.log(`   - Fator variação: ${variationFactor.toFixed(3)} (${(variationFactor * 100).toFixed(1)}% do valor)`);
      console.log(`   - Valor com variação: ${weightedAverage.toFixed(2)} × ${variationFactor.toFixed(3)} = ${variedValue.toFixed(2)}`);
      
      tempValues.push(variedValue);
      tempTotal += variedValue;
    }
    
    // Normalizar para que a soma seja igual ao total esperado
    console.log(`\n🔧 Normalizando: Total calculado ${tempTotal.toFixed(2)} → Total esperado ${totalExpected.toFixed(2)}`);
    const normalizationFactor = totalExpected / tempTotal;
    console.log(`📊 Fator de normalização: ${normalizationFactor.toFixed(4)}`);
    
    for (let i = 0; i < months; i++) {
      const normalizedValue = tempValues[i] * normalizationFactor;
      const finalValue = Math.round(normalizedValue * 100) / 100;
      console.log(`   Mês ${i + 1}: ${tempValues[i].toFixed(2)} × ${normalizationFactor.toFixed(4)} = ${finalValue.toFixed(2)}`);
      
      monthlyValues.push(finalValue);
      runningTotal += finalValue;
    }
    
    console.log("📊 Valores mensais antes do bônus:", monthlyValues);
    console.log("💰 Total antes do bônus:", runningTotal.toFixed(2));
    
    // Adicionar bônus aleatório
    const bonusPercentage = generateRandomBonus();
    const bonusAmount = Math.round((runningTotal * bonusPercentage / 100) * 100) / 100;
    
    console.log("🎁 Percentual de bônus:", bonusPercentage.toFixed(5) + "%");
    console.log("💎 Valor do bônus:", bonusAmount);
    
    // Aplicar bônus proporcionalmente
    const finalMonthlyValues = monthlyValues.map((value, index) => {
      const proportion = value / runningTotal;
      const monthBonus = Math.round((bonusAmount * proportion) * 100) / 100;
      return value + monthBonus;
    });
    
    const finalTotal = finalMonthlyValues.reduce((sum, value) => sum + value, 0);
    
    console.log("📊 Valores mensais finais:", finalMonthlyValues);
    console.log("🎯 Total final:", finalTotal.toFixed(2));
    console.log("📈 Diferença do valor de entrada:", (finalTotal - inputRevenue).toFixed(2));
    
    return {
      monthlyValues: finalMonthlyValues,
      totalSum: finalTotal,
      monthsCount: months,
      bonusPercentage: bonusPercentage,
      bonusAmount: bonusAmount,
      inputRevenue: inputRevenue,
      monthlyAverage: monthlyAverage
    };
  };

  // Função para buscar informações do CNPJ via proxy PHP (mesma solução do AnnualRevenue)
  const fetchCnpjInfo = async (cnpjValue) => {
    try {
      // Limpa o CNPJ, deixando apenas números
      const cleanCnpj = cnpjValue.replace(/\D/g, '');
      
      console.log(`🔍 Consultando CNPJ ${cleanCnpj} via proxy PHP...`);
      
      // Usar o proxy PHP que utiliza cURL para fazer a requisição à ReceitaWS
      // Mesma solução implementada no AnnualRevenue.jsx
      const response = await fetch('/api/cnpj_proxy.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          cnpj: cleanCnpj
        })
      });
      
      if (!response.ok) {
        throw new Error(`Erro na consulta do CNPJ: HTTP ${response.status} - ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Verificar se a resposta contém erro
      if (data.status === 'ERROR') {
        throw new Error(data.message || 'CNPJ não encontrado ou inválido');
      }
      
      console.log(`✅ CNPJ encontrado: ${data.nome}`);
      
      // Mapear os dados da ReceitaWS para o formato esperado pelo sistema
      return {
        cnpj: data.cnpj,
        nome_fantasia: data.fantasia || data.nome,
        empresa: {
          razao_social: data.nome
        },
        municipio: {
          descricao: data.municipio
        },
        uf: data.uf,
        situacao: data.situacao,
        porte: data.porte,
        natureza_juridica: data.natureza_juridica,
        atividade_principal: data.atividade_principal,
        atividades_secundarias: data.atividades_secundarias,
        qsa: data.qsa,
        logradouro: data.logradouro,
        numero: data.numero,
        complemento: data.complemento,
        bairro: data.bairro,
        cep: data.cep,
        email: data.email,
        telefone: data.telefone,
        data_situacao: data.data_situacao,
        capital_social: data.capital_social,
        abertura: data.abertura,
        ultima_atualizacao: data.ultima_atualizacao,
        simples: data.simples,
        simei: data.simei
      };
      
    } catch (err) {
      console.error('❌ Erro ao buscar informações do CNPJ:', err);
      throw err;
    }
  };

  // Função para formatar CNPJ
  const formatCnpj = (cnpj) => {
    if (!cnpj) return '';
    const cleaned = cnpj.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/);
    if (match) {
      return `${match[1]}.${match[2]}.${match[3]}/${match[4]}-${match[5]}`;
    }
    return cnpj;
  };

  // Função para formatar valor monetário brasileiro
  const formatCurrency = (value) => {
    if (!value) return "";
    const numericValue = parseFloat(value.replace(/\D/g, "")) / 100;
    return numericValue.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Função para processar dados
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!revenue || !cnpj || !startDate || !endDate) {
      setError("Por favor, preencha todos os campos obrigatórios.");
      return;
    }

    if (monthsCount <= 0 || monthsCount > 12) {
      setError("O intervalo de datas deve resultar entre 1 e 12 meses.");
      return;
    }

    setProcessing(true);
    setError(null);
    setResult(null);
    setCompanyInfo(null);

    try {
      const numericRevenue = parseFloat(revenue.replace(/\./g, '').replace(',', '.'));
      
      console.log("🎯 Iniciando processamento...");
      console.log("💰 Valor informado:", numericRevenue);
      console.log("📅 Quantidade de meses:", monthsCount);

      try {
        console.log("🔍 Buscando informações do CNPJ...");
        const cnpjInfo = await fetchCnpjInfo(cnpj);
        setCompanyInfo(cnpjInfo);
        console.log("✅ CNPJ encontrado:", cnpjInfo.empresa?.razao_social);
      } catch (err) {
        setError(`Erro ao consultar o CNPJ: ${err.message}`);
        setProcessing(false);
        return;
      }
      
      console.log("🎯 Iniciando distribuição variável local...");
      const distributionResult = distributeVariableRevenue(numericRevenue, monthsCount);
      
      const [startMonth, startYear] = startDate.split('/').map(num => parseInt(num));
      
      const monthlyValues = [];
      let currentMonth = startMonth;
      let currentYear = startYear;
      
      for (let i = 0; i < monthsCount; i++) {
        const monthKey = `${currentMonth.toString().padStart(2, '0')}/${currentYear}`;
        
        // Pegar o valor diretamente do array usando o índice
        const monthValue = distributionResult.monthlyValues[i];
        
        const formattedValue = monthValue.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        });
        
        monthlyValues.push({
          month: monthKey,
          value: formattedValue
        });
        
        currentMonth++;
        if (currentMonth > 12) {
          currentMonth = 1;
          currentYear++;
        }
      }
      
      const formattedTotal = distributionResult.totalSum.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      setResult({
        monthlyValues,
        totalSum: formattedTotal,
        monthsCount: monthsCount
      });
      
      setProcessing(false);
    } catch (err) {
      setError(`Erro ao processar: ${err.message}`);
      setCompanyInfo(null);
      setProcessing(false);
    }
  };

  // Handlers para formatação
  const handleRevenueFocus = () => {
    if (!isEditing) {
      setIsEditing(true);
      setRevenue(revenue.replace(/\./g, '').replace(',', '.'));
    }
  };

  const handleRevenueBlur = () => {
    setIsEditing(false);
    if (revenue) {
      const formatted = formatCurrency(revenue.replace('.', ','));
      setRevenue(formatted);
    }
  };

  const handleCnpjFocus = () => {
    if (!isCnpjEditing) {
      setIsCnpjEditing(true);
      setCnpj(cnpj.replace(/\D/g, ''));
    }
  };

  const handleCnpjBlur = () => {
    setIsCnpjEditing(false);
    if (cnpj) {
      const formatted = formatCnpj(cnpj);
      setCnpj(formatted);
    }
  };

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // Handlers para formatação de datas
  const handleStartDateChange = (e) => {
    const value = e.target.value;
    const formatted = formatDate(value);
    setStartDate(formatted);
  };

  const handleEndDateChange = (e) => {
    const value = e.target.value;
    const formatted = formatDate(value);
    setEndDate(formatted);
  };

  // Função para gerar e baixar CSV (idêntica à AnnualRevenue.jsx)
  const generateAndDownloadCSV = async () => {
    console.log("Iniciando geração e download do CSV");
    setCsvGenerating(true);
    
    try {
      if (!result || !result.monthlyValues || result.monthlyValues.length === 0) {
        console.error("Não há dados para gerar o CSV");
        alert("Por favor, calcule a distribuição primeiro!");
        setCsvGenerating(false);
        return;
      }
      
      console.log("Dados para o CSV:", result);
      
      // Criar o conteúdo do CSV
      let csvContent = "";
      
      // Adicionar cada linha no formato MMAAAA,VALOR
      result.monthlyValues.forEach((item) => {
        // Extrair mês e ano do formato MM/AAAA
        const [month, year] = item.month.split('/');
        
        // Remover o "R$" e formatação (pontos e vírgulas) do valor
        // Substituir vírgula por ponto para formato numérico internacional
        const valueWithDot = item.value.replace(/\./g, '').replace(',', '.');
        
        // Montar a linha no formato solicitado: MMAAAA,VALOR
        const csvLine = `${month}${year},${valueWithDot}\n`;
        csvContent += csvLine;
      });
      
      // Criar o blob com o conteúdo do CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      
      // Criar link para download
      const link = document.createElement('a');
      
      // Extrair o nome fantasia e formatar a data para o nome do arquivo
      const nomeFantasia = companyInfo.nome_fantasia || '';
      // Pegar o último mês dos valores (que é a data final)
      const dataFinal = result.monthlyValues[result.monthlyValues.length - 1].month.replace('/', '');
      
      // Formatar o nome do arquivo - só inclui o nome fantasia se existir
      const nomeCSV = nomeFantasia 
        ? `FATURAMENTO ${nomeFantasia} ${dataFinal}` 
        : `FATURAMENTO ${dataFinal}`;
      
      link.href = url;
      link.setAttribute('download', nomeCSV + '.csv');
      
      // Simular clique no link
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpar URL criada
      URL.revokeObjectURL(url);
      
      console.log("CSV gerado e baixado com sucesso");
      setCsvGenerating(false);
      return true;
    } catch (err) {
      console.error('Erro ao gerar ou baixar CSV:', err);
      console.error(err.stack);
      alert("Ocorreu um erro: " + err.message);
      setCsvGenerating(false);
      return false;
    }
  };

  // Função para iniciar edição dos valores
  const startEditingValues = () => {
    setEditingValues(true);
    // Copiar valores atuais para edição
    const currentValues = result.monthlyValues.map(item => ({
      month: item.month,
      value: item.value.replace(/\./g, '').replace(',', '.') // Converter para formato numérico
    }));
    setEditedValues(currentValues);
  };

  // Função para cancelar edição
  const cancelEditingValues = () => {
    setEditingValues(false);
    setEditedValues([]);
  };

  // Função para salvar valores editados e reprocessar
  const saveEditedValues = () => {
    console.log("💾 Salvando valores editados e reprocessando...");
    
    try {
      // Calcular novo total
      const newTotal = editedValues.reduce((sum, item) => sum + parseFloat(item.value), 0);
      console.log("📊 Novo total calculado:", newTotal.toFixed(2));
      
      // Formatar valores para exibição
      const formattedValues = editedValues.map(item => ({
        month: item.month,
        value: parseFloat(item.value).toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      }));
      
      // Atualizar resultado com novos valores
      const newResult = {
        ...result,
        monthlyValues: formattedValues,
        totalSum: newTotal.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }),
        isEdited: true // Flag para indicar que foi editado manualmente
      };
      
      setResult(newResult);
      setEditingValues(false);
      setEditedValues([]);
      
      console.log("✅ Valores atualizados com sucesso");
    } catch (error) {
      console.error("❌ Erro ao salvar valores editados:", error);
      alert("Erro ao salvar valores. Verifique se todos os valores são números válidos.");
    }
  };

  // Função para atualizar um valor editado
  const updateEditedValue = (index, newValue) => {
    const updatedValues = [...editedValues];
    updatedValues[index].value = newValue;
    setEditedValues(updatedValues);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-6xl mx-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to={createPageUrl("home")}>
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Faturamento Dinâmico</h1>
              <p className="text-gray-600">Distribuição automática baseada no intervalo de datas</p>
            </div>
          </div>
          <BarChart3 className="h-8 w-8 text-blue-600" />
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form Card */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Dados do Faturamento
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Display da Quantidade de Meses Calculada */}
                {monthsCount > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <Label className="text-blue-900 font-semibold">Quantidade de Meses Calculada</Label>
                    <p className="text-blue-800 text-lg font-bold">
                      {monthsCount} {monthsCount === 1 ? 'mês' : 'meses'}
                    </p>
                    <p className="text-blue-600 text-sm">
                      Baseado no intervalo entre as datas informadas
                    </p>
                  </div>
                )}

                {/* CNPJ */}
                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ da Empresa</Label>
                  <Input
                    id="cnpj"
                    type="text"
                    placeholder="00.000.000/0001-00"
                    value={cnpj}
                    onChange={(e) => setCnpj(e.target.value)}
                    onFocus={handleCnpjFocus}
                    onBlur={handleCnpjBlur}
                    className="font-mono"
                  />
                </div>

                {/* Período */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="startDate">Data Inicial</Label>
                    <Input
                      id="startDate"
                      type="text"
                      placeholder="MM/AAAA"
                      value={startDate}
                      onChange={handleStartDateChange}
                      maxLength={7}
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endDate">Data Final</Label>
                    <Input
                      id="endDate"
                      type="text"
                      placeholder="MM/AAAA"
                      value={endDate}
                      onChange={handleEndDateChange}
                      maxLength={7}
                      className="font-mono"
                    />
                  </div>
                </div>

                {/* Valor Total */}
                <div className="space-y-2">
                  <Label htmlFor="revenue">Valor Total do Faturamento</Label>
                  <Input
                    id="revenue"
                    type="text"
                    placeholder="0,00"
                    value={revenue}
                    onChange={(e) => setRevenue(e.target.value)}
                    onFocus={handleRevenueFocus}
                    onBlur={handleRevenueBlur}
                    className="font-mono text-lg"
                  />
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Calcular Distribuição
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Results Card */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Resultado da Distribuição
              </CardTitle>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <span className="text-red-700">{error}</span>
                  </div>
                </div>
              )}

              {result && (
                <div className="space-y-4">
                  {companyInfo && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="font-semibold text-blue-900 mb-2">Informações da Empresa</h3>
                      <p className="text-blue-800">{companyInfo.empresa?.razao_social}</p>
                      <p className="text-blue-600 text-sm">{formatCnpj(companyInfo.cnpj)}</p>
                    </div>
                  )}

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900">Distribuição por Mês</h3>
                      {!editingValues && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={startEditingValues}
                          className="flex items-center"
                        >
                          <Edit3 className="h-4 w-4 mr-1" />
                          Editar
                        </Button>
                      )}
                    </div>
                    
                    {editingValues ? (
                      // Modo de edição
                      <div className="space-y-2">
                        {editedValues.map((item, index) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-white rounded-lg border">
                            <span className="font-medium">{item.month}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-500">R$</span>
                              <Input
                                type="number"
                                step="0.01"
                                value={item.value}
                                onChange={(e) => updateEditedValue(index, e.target.value)}
                                className="w-32 text-right font-mono"
                              />
                            </div>
                          </div>
                        ))}
                        
                        {/* Botões de edição */}
                        <div className="flex justify-end space-x-2 pt-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={cancelEditingValues}
                            className="flex items-center"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Cancelar
                          </Button>
                          <Button
                            size="sm"
                            onClick={saveEditedValues}
                            className="flex items-center bg-green-600 hover:bg-green-700"
                          >
                            <Save className="h-4 w-4 mr-1" />
                            Salvar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // Modo de visualização
                      <div className="space-y-2">
                        {result.monthlyValues.slice(0, monthsCount).map((item, index) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                            <span className="font-medium">{item.month}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono">R$ {item.value}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyToClipboard(item.value, index)}
                              >
                                {copiedIndex === index ? (
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="border-t pt-3">
                      <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                        <span className="font-bold text-green-900">Total</span>
                        <span className="font-bold font-mono text-green-900">R$ {result.totalSum}</span>
                      </div>
                      
                      {/* Indicador se foi editado */}
                      {result.isEdited && (
                        <div className="flex items-center justify-center p-2 mt-2 bg-blue-50 rounded-lg">
                          <span className="text-blue-800 text-sm">
                            ✏️ Valores editados manualmente
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-4">
                    <Button
                      onClick={generateAndDownloadCSV}
                      disabled={csvGenerating}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {csvGenerating ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <FileSpreadsheet className="h-4 w-4 mr-2" />
                      )}
                      {csvGenerating ? 'Gerando...' : 'Baixar CSV'}
                    </Button>
                    <Button
                      onClick={() => {
                        // Criar PDF com layout idêntico ao UpdateAnnualRevenue.jsx
                        const doc = new jsPDF();
                        
                        // Configurar fonte e tamanho
                        doc.setFont('helvetica', 'bold');
                        doc.setFontSize(16);
                        
                        // Título centralizado
                        doc.text('DECLARAÇÃO DE FATURAMENTO', doc.internal.pageSize.getWidth() / 2, 30, { align: 'center' });
                        
                        // Configurar fonte para o corpo do texto
                        doc.setFont('helvetica', 'normal');
                        doc.setFontSize(12);
                        
                        // Texto da declaração
                        const razaoSocial = companyInfo.empresa?.razao_social || '';
                        const cnpjFormatado = formatCnpj(companyInfo.cnpj);
                        
                        // Layout alinhado à esquerda com as margens da tabela (IGUAL UpdateAnnualRevenue)
                        const tableStartX = 25; // Mesma posição inicial da tabela
                        
                        const startYManual = 50;
                        let currentY = startYManual;
                        const lineHeight = 6;
                        
                        // LINHA 1: "Declaro para os devidos fins que a Empresa" (alinhado à esquerda)
                        doc.setFont('helvetica', 'normal');
                        doc.text("Declaro para os devidos fins que a Empresa", tableStartX, currentY);
                        
                        // LINHA 2: Nome da empresa (alinhado à esquerda, em NEGRITO)
                        currentY += lineHeight;
                        doc.setFont('helvetica', 'bold');
                        doc.text(razaoSocial, tableStartX, currentY);
                        
                        // LINHA 3: "inscrito no CNPJ sob nº {CNPJ} teve como faturamento nos últimos X" (alinhado à esquerda)
                        currentY += lineHeight;
                        doc.setFont('helvetica', 'normal');
                        const linha3Inicio = "inscrito no CNPJ sob nº ";
                        const linha3Fim = ` teve como faturamento nos últimos ${monthsCount}`;
                        
                        // Texto inicial
                        doc.text(linha3Inicio, tableStartX, currentY);
                        const linha3InicioWidth = doc.getTextWidth(linha3Inicio);
                        
                        // CNPJ em NEGRITO
                        doc.setFont('helvetica', 'bold');
                        const cnpjWidth = doc.getTextWidth(cnpjFormatado);
                        doc.text(cnpjFormatado, tableStartX + linha3InicioWidth, currentY);
                        
                        // Texto final da linha 3
                        doc.setFont('helvetica', 'normal');
                        doc.text(linha3Fim, tableStartX + linha3InicioWidth + cnpjWidth, currentY);
                        
                        // LINHA 4: "meses os valores discriminados abaixo:" (alinhado à esquerda)
                        currentY += lineHeight;
                        doc.text("meses os valores discriminados abaixo:", tableStartX, currentY);
                        
                        // Ajustar posição Y para tabela, considerando o espaço ocupado pelo texto introdutório
                        let y = currentY + 20;
                        
                        // Tabela de faturamento manual - EXATAMENTE igual UpdateAnnualRevenue
                        const tableLineHeight = 10;
                        const colWidths = [80, 80]; // Larguras das colunas
                        const startX = 25; // Posição inicial X
                        
                        // Cabeçalho da tabela
                        doc.setFont('helvetica', 'bold');
                        doc.text('MÊS', startX, y);
                        doc.text('VALOR', startX + colWidths[0], y);
                        
                        // Linha sob o cabeçalho com ESPAÇAMENTO
                        y += 2;
                        doc.line(startX - 5, y, startX + colWidths[0] + colWidths[1] + 5, y);
                        y += 5;
                        
                        // Corpo da tabela
                        doc.setFont('helvetica', 'normal');
                        
                        // Adicionar os valores mensais
                        result.monthlyValues.forEach((item, index) => {
                          // Alternar cores de fundo (emulação)
                          if (index % 2 === 0) {
                            doc.setFillColor(245, 245, 245);
                            doc.rect(startX - 5, y - 5, colWidths[0] + colWidths[1] + 10, tableLineHeight, 'F');
                          }
                          
                          doc.text(item.month, startX, y);
                          doc.text(`R$ ${item.value}`, startX + colWidths[0], y);
                          y += tableLineHeight;
                        });
                        
                        // Adicionar linha de total
                        doc.setFont('helvetica', 'bold');
                        y += 2;
                        doc.line(startX - 5, y, startX + colWidths[0] + colWidths[1] + 5, y);
                        y += 5;
                        doc.text('TOTAL', startX, y);
                        const formattedTotal = result.totalSum.toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        });
                        doc.text(`R$ ${formattedTotal}`, startX + colWidths[0], y);
                        
                        // Adicionar local e data na parte inferior
                        let municipio = companyInfo.municipio?.descricao || '';
                        const uf = companyInfo.uf || '';
                        
                        // Formatar o município para ter apenas as primeiras letras maiúsculas
                        if (municipio) {
                          municipio = municipio.toLowerCase().split(' ').map(word => 
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ');
                        }
                        
                        // Obter data atual por extenso
                        const dataAtual = new Date();
                        const dia = dataAtual.getDate();
                        const meses = [
                          'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
                          'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
                        ];
                        const mes = meses[dataAtual.getMonth()];
                        const ano = dataAtual.getFullYear();
                        
                        const localData = `${municipio}/${uf}, ${dia} de ${mes} de ${ano}`;
                        
                        // Posicionar mais próximo da tabela conforme solicitado
                        const finalY = y + 20;
                        
                        doc.text(localData, doc.internal.pageSize.getWidth() / 2, finalY, { align: 'center' });
                        
                        // Ajustando a posição do carimbo para a posição ideal
                        const carimboPosY = finalY + 24;
                        
                        // Configurar fonte Times tamanho 8 e itálico para o carimbo do contador
                        doc.setFont('times', 'italic');
                        doc.setFontSize(8);
                        
                        // Nome com primeiras letras maiúsculas
                        doc.text('Francisco de Assis Rodrigues Fraga', doc.internal.pageSize.getWidth() / 2, carimboPosY, { align: 'center' });
                        doc.text('CRC: 004432/O-0', doc.internal.pageSize.getWidth() / 2, carimboPosY + 5, { align: 'center' });
                        doc.text('Contador', doc.internal.pageSize.getWidth() / 2, carimboPosY + 10, { align: 'center' });
                        
                        // Restaurar fonte padrão
                        doc.setFont('helvetica', 'normal');
                        doc.setFontSize(12);
                        
                        // Nome do arquivo
                        const nomeFantasia = companyInfo.nome_fantasia || '';
                        const dataFinal = result.monthlyValues[result.monthlyValues.length - 1].month.replace('/', '');
                        const nomePDF = nomeFantasia 
                          ? `FATURAMENTO ${nomeFantasia} ${dataFinal}` 
                          : `FATURAMENTO ${dataFinal}`;
                          
                        doc.save(nomePDF + '.pdf');
                      }}
                      className="flex-1"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Gerar PDF
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </motion.div>
    </div>
  );
}
